import { PayloadAction } from "@reduxjs/toolkit";
import { put, takeLatest, call, all, select } from "redux-saga/effects";

import { authenticationService } from "infrastructure/authentication/";
import { authenticateHttpClient } from "infrastructure/http";
import { changeTenant } from "ui/app/app.actions";

import { createUserRoleAccessData } from "./Authorization.helpers";
import { userActions } from "./UserProvider.actions";
import { getUserProfile, IUserProfileGetResponse } from "./UserProvider.api";
import { IIdentity, IRole, PlexUser, selectors } from "./UserProvider.state";

const createIdentity = (user: PlexUser): IIdentity => {
  return {
    id: user.profile.accountId,
    tenantId: user.profile.tenantId,
    profile: {
      firstName: user.profile.givenName,
      lastName: user.profile.familyName
    }
  };
};

function* trySignInExecute() {
  try {
    const user = yield call([authenticationService, authenticationService.getUser]);

    if (authenticationService.isValidUser(user)) {
      authenticateHttpClient(user.access_token);

      yield put(userActions.signInCompleted(createIdentity(user)));
    } else {
      yield put(userActions.remoteAuthenticationRequired());
    }
  } catch (e) {
    yield put(userActions.signInError());
  }
}

function* signInExecute() {
  try {
    const user = yield call([authenticationService, authenticationService.getUser]);

    if (authenticationService.isValidUser(user)) {
      authenticateHttpClient(user.access_token);

      yield put(userActions.signInCompleted(createIdentity(user)));
    } else {
      yield call([authenticationService, authenticationService.startSignIn]);
    }
  } catch (e) {
    yield put(userActions.signInError());
  }
}

function* completeSignInExecute() {
  try {
    const user = yield call([authenticationService, authenticationService.completeSignIn]);

    if (authenticationService.isValidUser(user)) {
      authenticateHttpClient(user.access_token);

      yield put(userActions.signInCompleted(createIdentity(user)));
    } else {
      yield put(userActions.signInError());
    }
  } catch (e) {
    yield put(userActions.signInError());
  }
}

function* signOutExecute() {
  try {
    yield call([authenticationService, authenticationService.signOut]);
  } catch (e) {}
}

function* authorizeExecute() {
  try {
    const profile: IUserProfileGetResponse = yield call(getUserProfile);
    const tenantId: string = yield select(selectors.getTenantId);

    const userRoles: IRole[] = profile.securityRoles.map(r => ({
      role: r.securityRoleId,
      tenant: r.tenantId,
      enterprise: r.enterpriseId
    }));

    const userRoleAccessData = createUserRoleAccessData(userRoles, tenantId);

    yield put(
      userActions.authorizeCompleted({
        roles: userRoles,
        tenants: profile.tenants,
        userRoleAccessData: userRoleAccessData
      })
    );
  } catch (e) {
    yield put(userActions.authorizeError());
  }
}

function* changeTenantExecute({ payload }: PayloadAction<string>) {
  const roles: IRole[] = yield select(selectors.getRoles);
  const userRoleAccessData = createUserRoleAccessData(roles, payload);

  yield put(userActions.tenantChanged({ tenantId: payload, userRoleAccessData: userRoleAccessData }));
}

function* actionWatcher() {
  yield takeLatest(userActions.authorize.toString(), authorizeExecute);
  yield takeLatest(userActions.trySignIn.toString(), trySignInExecute);
  yield takeLatest(userActions.signIn.toString(), signInExecute);
  yield takeLatest(userActions.completeSignIn.toString(), completeSignInExecute);
  yield takeLatest(userActions.signOut.toString(), signOutExecute);
  yield takeLatest(changeTenant.toString(), changeTenantExecute);
}

export function* userSaga() {
  yield all([actionWatcher()]);
}
